import { Alert, SkipNav } from '@ver-uds/uswds-react';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { PageHeader } from '@ver-uds/react';
import ReactGA from 'react-ga4';
import { EveRoutes } from '@ver-uds/e-verify';
import DashboardUserProfileCard from './DashboardUserProfileCard/DashboardUserProfileCard';
import DashboardUserAccountsSection from './DashboardUserAccountsSection/DashboardUserAccountsSection';
import { useDashboard } from '../../context/Dashboard/DashboardContext';
import DashboardLinkModal from './DashboardLinkModal/DashboardLinkModal';
import DashboardEditUserProfileCard from './DashboardEditUserProfileCard/DashboardEditUserProfileCard';
import DashboardForgotPasswordModal from './DashboardForgotPasswordModal/DashboardForgotPasswordModal';
import DashboardLinkAccountsSection from './DashboardLinkAccountsSection/DashboardLinkAccountsSection';
import DashboardUnlinkModal from './DashboardUnlinkModal/DashboardUnlinkModal';
import { hideUnlinkedUserAccounts } from '../../utils/dashboardUtils';

const Dashboard = (): JSX.Element => {
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState<boolean>(false);
  const [linkUserLogonID, setLinkUserLogonID] = useState<string>('');
  const [showUnlinkModal, setShowUnlinkModal] = useState<boolean>(false);

  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState<boolean>(false);

  // Get users and profile from dashboard context
  const { users, legacyUsers, usersAreLoading, profile, reloadProfile } = useDashboard();

  const filteredLegacyUsersUnlinkedAccounts = hideUnlinkedUserAccounts(legacyUsers);

  const triggerPasswordReset = (): void => {
    setShowLinkModal(false);
    setShowForgotPasswordModal(true);
  };

  const onFinishedEditing = (submitted = false): void => {
    setShowEditProfile(false);

    if (submitted) {
      reloadProfile();
    }
  };

  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      page: window.location.pathname + window.location.search,
      pageTitle: 'E-Verify - Dashboard',
    });
  }, []);

  useEffect(() => {
    const localStorageUserID = localStorage.getItem('MFAMigrationComplete');
    if (!!localStorageUserID && users.find((user) => user.userId === localStorageUserID)) {
      localStorage.removeItem('MFAMigrationComplete');
    }
  }, [users]);

  if (!usersAreLoading && users.length < 1) {
    window.location.assign(EveRoutes.DASHBOARD_NOT_FOUND);
  }

  return (
    <>
      <Helmet>
        <title>Dashboard</title>
      </Helmet>
      <PageHeader>Dashboard</PageHeader>
      <div className="grid-container margin-top-4 margin-bottom-4">
        <div className="grid-row">
          <div className="grid-col">
            <SkipNav.MainContent>
              {profile && (
                <>
                  <DashboardLinkModal
                    isOpen={showLinkModal}
                    setIsOpen={setShowLinkModal}
                    selectedUser={linkUserLogonID}
                    setSelectedUser={setLinkUserLogonID}
                    triggerPasswordReset={triggerPasswordReset}
                  />
                  <DashboardUnlinkModal
                    isOpen={showUnlinkModal}
                    setIsOpen={setShowUnlinkModal}
                    selectedUser={linkUserLogonID}
                  />
                </>
              )}
              {profile && (
                <DashboardForgotPasswordModal isOpen={showForgotPasswordModal} setIsOpen={setShowForgotPasswordModal} />
              )}
              {users.length > 1 && (
                <Alert status="info" slim className="margin-bottom-3">
                  <Alert.Text className="tablet:grid-col-9">
                    Your user accounts are linked to your user profile. Your name, email, and phone number will be the
                    same for all user accounts.
                  </Alert.Text>
                </Alert>
              )}
              {showEditProfile ? (
                <DashboardEditUserProfileCard onFinishedEditing={onFinishedEditing} />
              ) : (
                <DashboardUserProfileCard onEditClicked={(): void => setShowEditProfile(true)} />
              )}
              <hr />
              <DashboardUserAccountsSection setShowLinkModal={setShowLinkModal} />
              {filteredLegacyUsersUnlinkedAccounts.length >= 1 && (
                <DashboardLinkAccountsSection
                  setShowLinkModal={setShowLinkModal}
                  setLinkUserLogonID={setLinkUserLogonID}
                  setShowUnlinkModal={setShowUnlinkModal}
                  triggerPasswordReset={triggerPasswordReset}
                  filteredLegacyUsersUnlinkedAccounts={filteredLegacyUsersUnlinkedAccounts}
                />
              )}
            </SkipNav.MainContent>
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
